import * as React from "react";
import { Avatar } from "../display/Avatar";
import { Input } from "./Input";

/**
 * Type-to-search person picker: a search field that lists the people whose name or email matches,
 * below it, in the Menu's card. Picking one calls `onPick` and clears the field for the next.
 * Follows the ARIA combobox pattern (arrow keys move, Enter picks, Escape closes), so it replaces
 * a long Select of names: you type a few letters instead of scrolling.
 */
export interface PersonOption {
  id: string;
  name: string;
  email?: string;
}

export interface PersonPickerProps {
  /** Everyone who can be picked; the caller leaves out people already added. */
  options: PersonOption[];
  onPick: (person: PersonOption) => void;
  /** Accessible name for the search field. */
  inputLabel: string;
  placeholder?: string;
  /** Shown in the list when nothing matches. Default "No one matches". */
  emptyText?: string;
  /** Most matches listed at once. Default 8. */
  maxResults?: number;
  disabled?: boolean;
  className?: string;
}

/** From the start of a word, not anywhere: in an org everyone's email shares a domain, so "na"
 * would otherwise match every member through "navigo". The whole name counts as one word too, so
 * "jim n" finds Jim Nance. */
export function personMatches(person: PersonOption, q: string): boolean {
  const name = person.name.toLowerCase();
  const email = (person.email ?? "").toLowerCase();
  return name.startsWith(q) || name.split(/\s+/).some((w) => w.startsWith(q)) || email.startsWith(q);
}

export function PersonPicker({
  options,
  onPick,
  inputLabel,
  placeholder,
  emptyText = "No one matches",
  maxResults = 8,
  disabled,
  className,
}: PersonPickerProps): JSX.Element {
  const listId = React.useId();
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);

  const matches = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return options.filter((o) => personMatches(o, q)).slice(0, maxResults);
  }, [options, query, maxResults]);
  const showList = open && query.trim() !== "";

  function pick(person: PersonOption) {
    onPick(person);
    setQuery("");
    setActive(0);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      if (matches.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((a) => (a + step + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      if (showList && matches[active]) {
        e.preventDefault();
        pick(matches[active]);
      }
    } else if (e.key === "Escape") {
      if (showList) {
        e.preventDefault();
        setOpen(false);
      } else if (query) {
        setQuery("");
      }
    }
  }

  const optionId = (i: number) => `${listId}-option-${i}`;

  return (
    <div className={["gk-personpicker", className].filter(Boolean).join(" ")}>
      <Input
        icon="magnifying-glass"
        role="combobox"
        aria-label={inputLabel}
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && matches[active] ? optionId(active) : undefined}
        autoComplete="off"
        placeholder={placeholder}
        disabled={disabled}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      {showList ? (
        <div className="gk-menu gk-personpicker__list" id={listId} role="listbox" aria-label={inputLabel}>
          {matches.length === 0 ? (
            <div className="gk-personpicker__empty">{emptyText}</div>
          ) : (
            matches.map((person, i) => (
              <div
                key={person.id}
                id={optionId(i)}
                role="option"
                aria-selected={i === active}
                className="gk-menuitem gk-personpicker__option"
                // mousedown, not click: the field's blur would close the list before a click lands.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(person);
                }}
                onMouseEnter={() => setActive(i)}
              >
                <Avatar name={person.name} size={24} />
                <span className="gk-personpicker__text">
                  <span className="gk-personpicker__name">{person.name}</span>
                  {person.email && person.email !== person.name ? (
                    <span className="gk-personpicker__email">{person.email}</span>
                  ) : null}
                </span>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
